import { useState } from "react";
import { makeAssistantToolUI } from "@assistant-ui/react";
import type { ToolCallMessagePartComponent } from "@assistant-ui/react";
import { CheckCircle2, CircleStop, Loader2, XCircle } from "lucide-react";
import { Button } from "~/components/ui/button";
import { ToolCard } from "./ToolCard";

interface PipelineCancellationArgs {
  run_id: string;
  run_name?: string;
  workbench_id: number;
}

const CANCELLATION_CONFIRMED = JSON.stringify({ kind: "approved" });
const CANCELLATION_DENIED = JSON.stringify({ kind: "denied" });

const PipelineCancellationRender: ToolCallMessagePartComponent<
  PipelineCancellationArgs,
  string
> = ({ args, result, addResult }) => {
  const [decision, setDecision] = useState<"confirmed" | "cancelled" | null>(null);

  const isLoading = !args || typeof args.run_id !== "string";
  const isSubmitted = decision !== null || result != null;
  const effectiveDecision = isSubmitted
    ? (decision ?? (result === CANCELLATION_CONFIRMED ? "confirmed" : "cancelled"))
    : null;

  const handleConfirm = () => {
    if (decision) return;
    setDecision("confirmed");
    addResult(CANCELLATION_CONFIRMED);
  };

  const handleKeepRunning = () => {
    if (decision) return;
    setDecision("cancelled");
    addResult(CANCELLATION_DENIED);
  };

  return (
    <ToolCard
      label="Cancel Pipeline Run"
      color="rose"
      isLoading={isLoading}
      loadingText="Preparing pipeline cancellation..."
      isSubmitted={isSubmitted}
    >
      {args && <CancellationDetails args={args} />}

      {isSubmitted ? (
        <div className="flex items-center gap-2 text-sm mt-3">
          {effectiveDecision === "confirmed" ? (
            <>
              <CheckCircle2 className="size-4 text-rose-600" />
              <span className="text-rose-600">Cancellation confirmed</span>
              {result == null && <Loader2 className="size-3.5 animate-spin ml-1" />}
            </>
          ) : (
            <>
              <XCircle className="size-4 text-gray-500" />
              <span className="text-gray-500">Cancellation aborted — run continues</span>
            </>
          )}
        </div>
      ) : (
        <div className="flex gap-2 mt-4">
          <Button size="sm" variant="destructive" onClick={handleConfirm} className="gap-1.5">
            <CircleStop className="size-3.5" />
            Confirm Cancel
          </Button>
          <Button size="sm" variant="outline" onClick={handleKeepRunning}>
            Keep Running
          </Button>
        </div>
      )}
    </ToolCard>
  );
};

function CancellationDetails({ args }: { args: PipelineCancellationArgs }) {
  return (
    <div className="space-y-2">
      {args.run_name && <DetailRow label="Run Name" value={args.run_name} />}
      <DetailRow label="Run ID" value={args.run_id} />
      {args.workbench_id != null && (
        <DetailRow label="Workbench" value={String(args.workbench_id)} />
      )}
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2 text-sm">
      <span className="text-gray-500 shrink-0 min-w-[120px]">{label}:</span>
      <span className="text-gray-900 break-all">{value}</span>
    </div>
  );
}

export const PipelineCancellationTool = makeAssistantToolUI<PipelineCancellationArgs, string>({
  toolName: "pipeline_cancel_run",
  render: PipelineCancellationRender,
});
